import { useRef, useEffect, useCallback } from "react";
import { Renderer, Camera, Transform, Plane, Mesh, Program } from "ogl";
import { prefersReducedMotion } from "../CivixButton/buttonTokens";

// We'll reuse the glassVertexShader, or write a very simple pass-through.
const cardVertexShader = `
  attribute vec2 uv;
  attribute vec2 position;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const cardFragmentShader = `
  precision highp float;

  uniform vec2 uResolution;
  uniform vec2 uMouse;
  uniform float uHover;
  uniform float uTime;
  uniform vec3 uColorBase;
  uniform vec3 uColorHighlight;

  varying vec2 vUv;

  void main() {
    vec2 res = max(uResolution, vec2(1.0));
    vec2 p = vUv;
    
    // Slight aspect ratio correction for circular glow
    vec2 pos = p;
    pos.x *= res.x / res.y;

    vec2 mouseP = uMouse / res;
    mouseP.x *= res.x / res.y;

    float t = uTime * 0.15;

    // Soft animated mesh gradient background
    float mesh = sin(p.x * 3.0 + t) * sin(p.y * 2.0 - t * 0.5) * 0.5 + 0.5;
    vec3 fluid = mix(uColorBase, uColorHighlight, mesh * 0.15);

    // Subtle edge highlight that tracks mouse
    float dist = length(pos - mouseP);
    float glow = exp(-dist * 3.0) * uHover;

    // Combine
    fluid += uColorHighlight * glow * 0.2;

    // Add a delicate grain/noise to make it look premium
    float noise = fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    fluid -= noise * 0.015;

    gl_FragColor = vec4(fluid, 1.0);
  }
`;

/**
 * WebGL glass shader for CivixCard primary variant.
 */
export default function useCardShader({ enabled, rootRef, baseColor, highlightColor }) {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const programRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(0);
  const mouseRef = useRef([0, 0]);

  const syncCanvasSize = useCallback(() => {
    const root = rootRef.current;
    const renderer = rendererRef.current;
    const program = programRef.current;
    if (!root || !renderer || !program) return;

    const { width, height } = root.getBoundingClientRect();
    if (width < 1 || height < 1) return;

    renderer.setSize(width, height);
    program.uniforms.uResolution.value = [width * renderer.dpr, height * renderer.dpr];
  }, [rootRef]);

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) return undefined;
    if (!rootRef.current || !canvasRef.current) return undefined;

    const renderer = new Renderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      dpr: Math.min(window.devicePixelRatio || 1, 2),
    });
    rendererRef.current = renderer;
    const { gl } = renderer;

    const camera = new Camera(gl, { left: -1, right: 1, top: 1, bottom: -1, near: 0.1, far: 10 });
    camera.position.z = 1;

    const scene = new Transform();
    const geometry = new Plane(gl, { width: 2, height: 2 });

    const program = new Program(gl, {
      vertex: cardVertexShader,
      fragment: cardFragmentShader,
      uniforms: {
        uResolution: { value: [1, 1] },
        uMouse: { value: [0, 0] },
        uHover: { value: 0 },
        uTime: { value: 0 },
        uColorBase: { value: baseColor },
        uColorHighlight: { value: highlightColor },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    programRef.current = program;

    const mesh = new Mesh(gl, { geometry, program });
    mesh.setParent(scene);

    syncCanvasSize();

    const ro = new ResizeObserver(() => syncCanvasSize());
    ro.observe(rootRef.current);

    const tick = (t) => {
      rafRef.current = requestAnimationFrame(tick);
      program.uniforms.uTime.value = t * 0.001;
      
      program.uniforms.uMouse.value[0] +=
        (mouseRef.current[0] - program.uniforms.uMouse.value[0]) * 0.1;
      program.uniforms.uMouse.value[1] +=
        (mouseRef.current[1] - program.uniforms.uMouse.value[1]) * 0.1;
      program.uniforms.uHover.value += (hoverRef.current - program.uniforms.uHover.value) * 0.1;
      
      renderer.render({ scene, camera });
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      rendererRef.current = null;
      programRef.current = null;
    };
  }, [enabled, rootRef, baseColor, highlightColor, syncCanvasSize]);

  const updatePointer = useCallback(
    (clientX, clientY) => {
      const root = rootRef.current;
      const renderer = rendererRef.current;
      if (!root || !renderer) return;

      const rect = root.getBoundingClientRect();
      const dpr = renderer.dpr;
      
      mouseRef.current = [(clientX - rect.left) * dpr, (rect.bottom - clientY) * dpr];
      hoverRef.current = 1;
    },
    [rootRef]
  );

  const handleMouseLeave = useCallback(() => {
    hoverRef.current = 0;
  }, []);

  return {
    canvasRef,
    updatePointer,
    handleMouseLeave,
    active: enabled && !prefersReducedMotion(),
  };
}
